/**
 * The v3 wire format embedded in exported PNGs (SPEC §4.1, §7.3): readable
 * JSON with ids dropped and selections as rating indices. Share links use the
 * more compact binary format in `link.ts` (decision 018).
 */
import * as z from 'zod/mini';

import { unsetRating } from '../model/access';
import type { IdGen } from '../model/ids';
import { normalizeColor, normalizeComment, normalizeName, normalizeTitle } from '../model/text';
import type { Category, KinklistDoc, Rating } from '../model/types';
import { LIMITS } from '../model/types';
import { ImportError } from './errors';

export type WireKink = [name: string, sel: number[], comment?: string];

export interface WireV3 {
  v: 3;
  t: string;
  r: [name: string, color: string][];
  c: { n: string; s: string[]; k: WireKink[] }[];
}

export interface HydrateContext {
  newId: IdGen;
  now: () => Date;
}

export function toWire(doc: KinklistDoc): WireV3 {
  const ratingIndex = new Map(doc.ratings.map((rating, i) => [rating.id, i]));
  return {
    v: 3,
    t: doc.title,
    r: doc.ratings.map((rating) => [rating.name, rating.color]),
    c: doc.categories.map((category) => ({
      n: category.name,
      s: category.roles.map((role) => role.name),
      k: category.kinks.map((kink): WireKink => {
        const sel = category.roles.map((role) => ratingIndex.get(kink.ratings[role.id] ?? '') ?? 0);
        return kink.comment === undefined ? [kink.name, sel] : [kink.name, sel, kink.comment];
      }),
    })),
  };
}

/** A string schema that normalizes its value, failing when `normalize` returns null. */
export function normalizedString(normalize: (value: string) => string | null, message: string) {
  return z.pipe(
    z.string(),
    z.transform((value: string, ctx) => {
      const result = normalize(value);
      if (result !== null) return result;
      ctx.issues.push({ code: 'custom', message, input: value });
      return z.NEVER;
    }),
  );
}

const wireName = normalizedString(normalizeName, 'blank name');
const wireColor = normalizedString(normalizeColor, 'invalid color');

const selection = z.array(z.int().check(z.nonnegative()));

export const wireSchema = z
  .object({
    v: z.literal(3),
    t: z.string().check(z.maxLength(LIMITS.title * 4)),
    r: z
      .array(z.tuple([wireName, wireColor]))
      .check(z.minLength(LIMITS.minRatings), z.maxLength(64)),
    c: z.array(
      z.object({
        n: wireName,
        s: z.array(wireName).check(z.minLength(LIMITS.minRoles), z.maxLength(LIMITS.maxRoles)),
        k: z.array(
          z.union([
            z.tuple([wireName, selection]),
            z.tuple([wireName, selection, z.string().check(z.maxLength(LIMITS.comment * 2))]),
          ]),
        ),
      }),
    ),
  })
  .check(
    z.superRefine((wire, ctx) => {
      wire.c.forEach((category, ci) => {
        category.k.forEach(([, sel], ki) => {
          const path = ['c', ci, 'k', ki];
          if (sel.length !== category.s.length) {
            ctx.addIssue({
              code: 'custom',
              message: 'selection count ≠ role count',
              path,
              input: sel,
            });
          }
          if (sel.some((index) => index >= wire.r.length)) {
            ctx.addIssue({
              code: 'custom',
              message: 'rating index out of range',
              path,
              input: sel,
            });
          }
        });
      });
    }),
  );

/** Validates untrusted wire JSON and hydrates it into a document with fresh ids. */
export function fromWire(input: unknown, ctx: HydrateContext): KinklistDoc {
  const parsed = wireSchema.safeParse(input);
  if (!parsed.success) throw new ImportError('invalid', z.prettifyError(parsed.error));
  const wire = parsed.data;
  const ratings: Rating[] = wire.r.map(([name, color]) => ({ id: ctx.newId(), name, color }));
  const unsetId = unsetRating(ratings).id;
  const categories = wire.c.map((c): Category => {
    const roles = c.s.map((name) => ({ id: ctx.newId(), name }));
    return {
      id: ctx.newId(),
      name: c.n,
      roles,
      kinks: c.k.map(([name, sel, comment]) => {
        const kink = {
          id: ctx.newId(),
          name,
          ratings: Object.fromEntries(
            roles.map((role, i) => [role.id, ratings[sel[i] ?? 0]?.id ?? unsetId]),
          ),
        };
        const text = comment === undefined ? undefined : normalizeComment(comment);
        return text === undefined ? kink : { ...kink, comment: text };
      }),
    };
  });
  return {
    v: 3,
    title: normalizeTitle(wire.t),
    ratings,
    categories,
    updatedAt: ctx.now().toISOString(),
  };
}

export function wireToJson(doc: KinklistDoc): string {
  return JSON.stringify(toWire(doc));
}
